import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, MessageSquare, User, Calendar } from "lucide-react";
import { useGeneralAdvice } from "@/hooks/useGeneralAdvice";
import { format, parseISO } from "date-fns";
import type { GeneralAdvice } from "@/types/monitoring";
import type { ParentType } from "@/lib/general-advice-api";

interface GeneralAdviceHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  parentType?: ParentType;
}

const HistorySection = ({ advice }: { advice: GeneralAdvice }) => {
  const formattedDate = advice.created_at
    ? format(parseISO(advice.created_at), "dd MMM yyyy")
    : "Unknown Date";

  return (
    <div className="space-y-4 mb-6">
      {/* Date Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-border" />
        <Badge
          variant="outline"
          className="bg-primary/5 border-primary/20 text-primary z-caption"
        >
          <Calendar className="h-3 w-3 mr-1" />
          {formattedDate}
        </Badge>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Tags */}
      <div className="flex items-center gap-2 flex-wrap">
        {advice.zarity_appointment_id && (
          <Badge
            variant="secondary"
            className="bg-muted/40 text-muted-foreground border-border/60 z-caption"
          >
            {advice.zarity_appointment_id}
          </Badge>
        )}
        {advice.doctor_name && (
          <Badge
            variant="secondary"
            className="bg-muted/40 text-muted-foreground border-border/60 z-caption"
          >
            <User className="h-3 w-3 mr-1" />
            {advice.doctor_name}
          </Badge>
        )}
      </div>

      {/* Advice List */}
      <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
        {advice.advices.length === 0 ? (
          <div className="text-center text-muted-foreground py-6 z-body">
            No advices recorded
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {advice.advices.map((adviceText, index) => (
              <div
                key={index}
                className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
              >
                <span className="z-caption text-muted-foreground min-w-[24px]">
                  {index + 1}.
                </span>
                <span className="z-body text-foreground">{adviceText}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const GeneralAdviceHistoryDialog = ({
  open,
  onOpenChange,
  appointmentId,
  parentType = "prescriptions",
}: GeneralAdviceHistoryDialogProps) => {
  // Fetch consolidated/history data
  const { data: historyDataRaw, isLoading } = useGeneralAdvice(
    appointmentId,
    true, // consolidated = true for history
    parentType
  );

  // Normalize data - history always returns array
  const historyData = Array.isArray(historyDataRaw)
    ? historyDataRaw
    : historyDataRaw
      ? [historyDataRaw]
      : [];

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0 border-border/60">
        <DialogHeader className="px-6 py-4 border-b border-border/60 flex flex-row items-center justify-between">
          <DialogTitle className="z-h2 text-foreground">
            General Advice - History
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 max-h-[600px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-muted rounded-full">
                  <MessageSquare className="h-8 w-8 text-muted-foreground" />
                </div>
              </div>
              <p className="z-h3 text-foreground">No History Available</p>
              <p className="mt-1">
                Past general advice records will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {historyData.map((advice) => (
                <HistorySection key={advice.id} advice={advice} />
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border/60 bg-card">
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
